import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, ShoppingCart, ShoppingBag, Users, Box, Boxes, ChartNoAxesColumnIncreasing,
  ShieldCheck, Lightbulb, Globe, Infinity as InfinityIcon, Monitor, Network, Database,
  Settings, Cloud, Tag, Gem, Truck, CalendarDays, UserRound, Zap, Star, Handshake,
  Factory, Code, Leaf, Layers, CheckCircle, Rocket,
} from 'lucide-react';
import { mediaUrl, safeLink } from '../../api/industryApi';

const icons = { cart: ShoppingCart, bag: ShoppingBag, users: Users, box: Box, boxes: Boxes,
  chart: ChartNoAxesColumnIncreasing, shield: ShieldCheck, lightbulb: Lightbulb, globe: Globe,
  infinity: InfinityIcon, monitor: Monitor, network: Network, database: Database,
  settings: Settings, cloud: Cloud, tag: Tag, gem: Gem, truck: Truck, calendar: CalendarDays,
  user: UserRound, zap: Zap, star: Star, handshake: Handshake, factory: Factory,
  code: Code, leaf: Leaf, layers: Layers, check: CheckCircle, rocket: Rocket };

export function IndustryIcon({ name }) {
  const Icon = icons[name] || Box;
  return <Icon className="ip-icon" strokeWidth={1.55} aria-hidden="true" />;
}

export function ContentLink({ url, children, className = '' }) {
  const href = safeLink(url);
  if (!href || !children) return null;
  const content = <>{children}<ArrowRight size={17} aria-hidden="true" /></>;
  return href.startsWith('/') ? <Link className={className} to={href}>{content}</Link>
    : <a className={className} href={href}>{content}</a>;
}

function Copy({ section }) {
  return <>
    {section.eyebrow && <p className="ip-eyebrow">{section.eyebrow}</p>}
    {section.title && <h2>{section.title}</h2>}
    {section.description && <p className="ip-description">{section.description}</p>}
    <ContentLink className="ip-button" url={section.buttonUrl}>{section.buttonLabel}</ContentLink>
  </>;
}

function Photo({ image, alt, caption, className = '' }) {
  const src = mediaUrl(image);
  if (!src) return null;
  return <figure className={`ip-photo ${className}`}>
    <img src={src} alt={alt || image.alternativeText || ''} loading="lazy" />
    {caption && <figcaption>{caption}</figcaption>}
  </figure>;
}

export function IndustryHero({ hero, name, detail }) {
  if (!hero) return null;
  const src = mediaUrl(hero.image);
  return <section className={`ip-hero ip-hero--${hero.theme === 'light' ? 'light' : 'dark'}`}>
    {src && <img className="ip-hero-image" src={src} alt={hero.imageAlt || hero.image?.alternativeText || ''}
      style={{ objectPosition: hero.imagePosition || 'center' }} fetchPriority="high" />}
    <div className="ip-wrap ip-hero-inner">
      <div className="ip-hero-copy">
        <nav className="ip-breadcrumb" aria-label="Breadcrumb"><Link to="/">Home</Link><span aria-hidden="true">/</span>
          {detail ? <><Link to="/industries">Industries</Link><span aria-hidden="true">/</span><span aria-current="page">{name}</span></> : <span aria-current="page">{name}</span>}
        </nav>
        {hero.eyebrow && <p className="ip-eyebrow">{hero.eyebrow}</p>}
        <h1>{hero.title}</h1>
        {hero.subtitle && <p className="ip-hero-subtitle">{hero.subtitle}</p>}
        {hero.description && <p className="ip-description">{hero.description}</p>}
        <ContentLink className="ip-button" url={hero.buttonUrl}>{hero.buttonLabel}</ContentLink>
      </div>
      {hero.sideNote && <p className="ip-side-note">{hero.sideNote}</p>}
    </div>
  </section>;
}

function Item({ item, kind, index }) {
  return <article className="ip-item">
    {kind === 'industries' && <Photo image={item.image} alt={item.imageAlt} />}
    <div className="ip-item-content">
      {kind === 'approach' || kind === 'process' ? <span className="ip-step">{String(index + 1).padStart(2, '0')}</span>
        : kind === 'technologies' || kind === 'clients' ? (mediaUrl(item.image) && <img className="ip-logo" src={mediaUrl(item.image)} alt={item.imageAlt || item.title} loading="lazy" />)
        : <IndustryIcon name={item.icon} />}
      <div className="ip-item-copy">
        {item.value && <strong className="ip-value">{item.value}</strong>}
        <h3>{item.title}</h3>
        {item.description && <p>{item.description}</p>}
        {item.bullets && <ul>{item.bullets.split('\n').filter(Boolean).map((line, i) => <li key={i}><ArrowRight size={14} aria-hidden="true" />{line}</li>)}</ul>}
        <ContentLink className="ip-text-link" url={item.linkUrl}>{item.linkLabel}</ContentLink>
      </div>
    </div>
  </article>;
}

export default function IndustrySection({ section }) {
  const { kind, items = [] } = section;
  const theme = ['white', 'soft', 'dark'].includes(section.theme) ? section.theme : 'white';
  if (kind === 'callout') return <section className="ip-section ip-callout ip-theme-dark">
    {mediaUrl(section.image) && <img className="ip-callout-bg" src={mediaUrl(section.image)} alt={section.imageAlt || ''} loading="lazy" />}
    <div className="ip-wrap ip-callout-inner"><div><Copy section={{ ...section, buttonUrl: null }} /></div>
      <ContentLink className="ip-button" url={section.buttonUrl}>{section.buttonLabel}</ContentLink></div>
  </section>;
  if (['opportunity', 'case-study', 'story', 'process'].includes(kind)) return <section className={`ip-section ip-${kind} ip-theme-${theme}`}>
    <div className="ip-wrap ip-split">
      <div className="ip-section-copy"><Copy section={section} />
        {kind === 'story' && <div className="ip-metrics">{items.map((item, i) => <div key={item.id || i}>
          {item.value ? <strong>{item.value}</strong> : <IndustryIcon name={item.icon} />}<p>{item.title}</p>{item.description && <small>{item.description}</small>}
        </div>)}</div>}
      </div>
      {kind === 'process' ? <div className="ip-grid">{items.map((item, i) => <Item key={item.id || i} item={item} kind={kind} index={i} />)}</div>
        : <div className={`ip-visual ${kind === 'opportunity' && items.some(item => item.image) ? 'ip-gallery' : ''}`}>
          <Photo image={section.image} alt={section.imageAlt} caption={section.imageCaption} />
          {kind === 'opportunity' && items.map((item, i) => <Photo key={item.id || i} image={item.image} alt={item.imageAlt} caption={item.title} />)}
        </div>}
    </div>
  </section>;
  if (!['industries','features','challenges','solutions','benefits','technologies','approach','clients'].includes(kind)) return null;
  return <section className={`ip-section ip-${kind} ip-theme-${theme}${kind === 'features' && items.length > 4 ? ' ip-features-wide' : ''}`}>
    <div className="ip-wrap">
      {(section.eyebrow || section.title || section.description) && <div className="ip-section-heading"><Copy section={section} /></div>}
      <div className="ip-grid">{items.map((item, i) => <Item key={item.id || i} item={item} kind={kind} index={i} />)}</div>
    </div>
  </section>;
}
